/**
 * Farm Manager AI Chat Service
 * High-level wrapper around the FarmManagerAgent for chat interactions
 */

import { getFarmManagerAgent } from "./farmManager/FarmManagerAgent";

/**
 * Initialize the AI service and warm up the underlying model
 * @returns {Promise<void>}
 */
export async function initializeAI() {
        const agent = getFarmManagerAgent();
        await agent.ensureModel();
}

/**
 * Sends a message to the AI and resolves once the complete response is available
 * @param {string} userId - User ID
 * @param {string} message - User's message
 * @param {Object} userContext - Context data from chatContextService
 * @param {Array} conversationHistory - Previous messages for context
 * @param {Object} options - Additional options (language, etc.)
 * @returns {Promise<string>} AI response text
 */
export async function sendMessage(
        userId,
        message,
        userContext,
        conversationHistory = [],
        options = {}
) {
        let fullText = "";
        await streamMessage(
                userId,
                message,
                userContext,
                conversationHistory,
                (chunk) => {
                        fullText += chunk;
                },
                options
        );
        return fullText;
}

/**
 * Streams a message response from the AI
 * @param {string} userId - User ID
 * @param {string} message - User's message
 * @param {Object} userContext - Context data from chatContextService
 * @param {Array} conversationHistory - Previous messages for context
 * @param {Function} onChunk - Callback function for each text chunk
 * @param {Object} options - Additional options (language, etc.)
 * @returns {Promise<string>} Complete AI response text
 */
export async function streamMessage(
        userId,
        message,
        userContext,
        conversationHistory = [],
        onChunk,
        options = {}
) {
        const agent = getFarmManagerAgent();
        const language = options.language || options.locale || "en";
        return agent.streamResponse({
                userId,
                message,
                userContext,
                conversationHistory,
                onChunk,
                language,
        });
}
